// BB-3 (docs/build_bot_platform.md §3.2.2): graceful model-tier degradation.
//
// On provider overload/timeout, fall back along a per-tier chain
// (deep → balanced → fast) instead of failing. Applied ONLY to transient
// errors — a content-policy block never degrades, it fails the turn. The tier
// actually used is reported so usage fidelity is preserved (usage_log /
// usageLogs record the tier that ran, not the tier that was requested).

// Order matters: deepest first. A tier falls back to the next entry.
// Keys are the client mock tier ids (mockData modelTiers) and match the
// model_tier slugs the PB seed will write (backend.md §5.3).
export const TIER_FALLBACK_CHAIN=[
  'tier_deep',
  'tier_balanced',
  'tier_fast'
];

export function getFallbackTier(tierId) {
  const index=TIER_FALLBACK_CHAIN.indexOf(tierId);

  if (index===-1 || index===TIER_FALLBACK_CHAIN.length-1) return null;

  return TIER_FALLBACK_CHAIN[index+1];
}

/**
 * Call a tier-bounded model task with degradation. `task(tierId)` must throw
 * retry-classified errors (modelRetry.js) for degradation to apply. Returns
 * `{result, tierUsed, degraded}`. If every tier in the chain fails, the last
 * error is rethrown.
 */
export async function withTierFallback(task,tierId) {
  let current=tierId;
  const tried=[];

  while (current) {
    try {
      const result=await task(current);
      return {result,tierUsed: current,degraded: current!==tierId,tried};
    } catch (error) {
      // Deterministic failures (policy, validation) never degrade.
      if (error?.code==='CONTENT_POLICY_BLOCKED') throw error;
      if (!error?.retryable) throw error;

      tried.push({tier: current,code: error.code});
      const next=getFallbackTier(current);

      if (!next) throw error;

      current=next;
    }
  }

  // Unreachable — the loop exits via return or throw.
  throw new Error('Tier fallback exhausted.');
}
